import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { viewDocument } from '../../../../../services/api';
import ThemeContext from '../../../../../ThemeContext';

const DocumentViewerModal = ({ show, onHide, document }) => {
  const { theme } = useContext(ThemeContext);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [placeholderContent, setPlaceholderContent] = useState('');

  useEffect(() => {
    if (show && document) {
      fetchDocument();
    } else {
      setDocumentUrl(null);
      setPlaceholderContent('');
    }
  }, [show, document]);

  const fetchDocument = async () => {
    try {
      setIsLoading(true);

      const response = await viewDocument(document.id);

      if (document.is_placeholder) {
        setPlaceholderContent(response.data.data.placeholder_content?.text || '');
        return;
      }

      const rawUrl = response.data.data.url;

      const ext = (
        document?.file_type ||
        document?.extension ||
        document?.name?.split('.').pop() ||
        ''
      ).toLowerCase();

      const browserViewExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
      const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

      if (browserViewExtensions.includes(ext)) {
        setDocumentUrl(rawUrl);
        return;
      }

      if (officeExtensions.includes(ext)) {
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`;
        setDocumentUrl(officeViewerUrl);
        return;
      }

      setDocumentUrl(rawUrl);
    } catch (error) {
      toast.error(`Failed to load document: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDocumentUrl(null);
    setPlaceholderContent('');
    onHide();
  };

  if (!document) return null;

  return (
    <Modal show={show} onHide={handleClose} centered size="xl" fullscreen>
      <Modal.Header
        style={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.textWhite,
          borderBottom: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Modal.Title style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
          {document.name}
        </Modal.Title>

        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.textWhite,
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0',
            marginLeft: 'auto',
          }}
        >
          ×
        </button>
      </Modal.Header>

      <Modal.Body
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          padding: 0,
          height: 'calc(100vh - 120px)',
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: 'clamp(10px, 2.5vw, 12px)',
            }}
          >
            Loading document...
          </div>
        ) : document.is_placeholder ? (
          <div
            style={{
              padding: '2rem',
              height: '100%',
              overflow: 'auto',
              backgroundColor: theme.colors.bgSecondary,
            }}
          >
            <h4 style={{ marginBottom: '1rem', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
              Placeholder Content
            </h4>

            {placeholderContent ? (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                  lineHeight: '1.6',
                }}
              >
                {placeholderContent}
              </div>
            ) : (
              <p style={{ color: theme.colors.textSecondary, fontSize: 'clamp(9px, 2vw, 10px)' }}>
                No content available. This placeholder is empty.
              </p>
            )}
          </div>
        ) : documentUrl ? (
  (() => {
    const ext = (
      document?.file_type ||
      document?.extension ||
      document?.name?.split('.').pop() ||
      ''
    ).toLowerCase();

    if (ext === 'pdf') {
      return (
        <object
          data={documentUrl}
          type="application/pdf"
          width="100%"
          height="100%"
          style={{ border: 'none' }}
        >
          <iframe
            src={documentUrl}
            title={document.name}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </object>
      );
    }

    return (
      <iframe
        src={documentUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        title={document.name}
      />
    );
  })()
) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: 'clamp(10px, 2.5vw, 12px)',
              color: theme.colors.textSecondary,
            }}
          >
            Document could not be loaded
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentViewerModal;